import { defineStore, storeToRefs } from 'pinia';
import { ref, computed } from 'vue';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useAppAuthData } from '@/stores/app/authData';
import { useAppThirdPartyPlugins } from '@/stores/app/thirdParty';
import { recruitInitNavbar } from '@/api/recruit';
import { getEnvironment } from '@/helpers/environment';

export const useAppPlatform = defineStore('appPlatform', () => {
  const { addErrorSnackbar } = useAppSnackbar();

  const platform = ref({});
  const currentJobId = ref('');
  const platformInitialized = ref(false);

  /* ==============
  computed
  ============== */

  const getHelpLinks = computed(() => [
    {
      name: 'Help Center',
      showSection: true,
      links: [
        {
          show: true,
          title: 'Read Help Articles',
          href: useAppThirdPartyPlugins().helpscoutSiteUrl,
          attributes: {
            target: '_blank',
          },
        },
      ],
    },
  ]);

  const dashboardNavLinks = computed(() => {
    const authDataStore = useAppAuthData();
    const { authData } = storeToRefs(authDataStore);

    const sections = [];

    sections.push(
      {
        name: 'Post Libraries',
        showSection: true,
        links: [
          {
            show: true,
            to: 'flagship',
            title: 'Flagship Posts',
            isNew: true,
          },
          {
            show: true,
            to: 'draft',
            title: 'Draft Posts',
          },
          {
            show: true,
            to: 'published',
            title: 'Published Posts',
          },
          {
            show: true,
            to: 'unpublished',
            title: 'Unpublished Posts',
          },
        ],
      },
    );

    if (authData.value.isApplicationReportingEnabled) {
      sections.push(
        {
          name: 'Requisition Libraries',
          showSection: true,
          links: [
            {
              show: true,
              to: 'draft-requisitions',
              title: 'Draft Requisitions',
            },
            {
              show: true,
              to: 'open-requisitions',
              title: 'Open Requisitions',
            },
            {
              show: true,
              to: 'closed-requisitions',
              title: 'Closed Requisitions',
            },
          ],
        },
      );
    }

    if (authData.value.jobProfileEnabled) {
      sections.push(
        {
          name: 'Job Profile Libraries',
          showSection: true,
          links: [
            { show: true, to: 'active-job-profiles', title: 'Active Job Profiles' },
            { show: true, to: 'inactive-job-profiles', title: 'Inactive Job Profiles' },
            { show: true, to: 'draft-job-profiles', title: 'Draft Job Profiles' },
            { show: true, to: 'job-profile-templates', title: 'Templates' },
          ],
        },
      );
    }

    if (authData.value.jobProfileEnabled && authData.value.acknowledgementsEnabled) {
      sections.push(
        {
          name: 'Job Profile Acknowledgements',
          showSection: true,
          links: [
            { show: true, to: 'employee-acknowledgements', title: 'Employee Acknowledgements' },
          ],
        },
      );
    }

    return sections;
  });

  const settingsBreadCrumbsLinks = computed(() => {
    const {
      canTeamAdmin,
      canIntegrations,
      noEditorEnabled,
    } = useAppAuthData().authData;

    const {
      showIntegrationsPage,
      canSeeTemplates,
      atsName,
    } = platform.value;

    return [
      {
        name: 'User',
        showSection: true,
        links: [
          {
            to: 'account',
            show: true,
            title: 'My Account',
          },
          {
            to: 'administrators',
            show: true,
            title: 'My Team',
          },
          {
            show: !noEditorEnabled,
            href: 'https://chromewebstore.google.com/detail/'
              + 'datapeople-anywhere/hhnemhalmbhdldblamkefkjnalpmadhm?hl=en&authuser=0',
            title: 'Datapeople Anywhere',
          },
          {
            show: true,
            href: '/logout',
            title: 'Log Out',
            attributes: {
              // This data test ID needs to be here for Canary scripts to work
              'data-testid': 'logout-link',
            },
          },
        ],
      },
      {
        name: 'Organization',
        showSection: true,
        links: [
          {
            to: 'integrations',
            show: canTeamAdmin && showIntegrationsPage && canIntegrations,
            title: `Connect ${atsName}`,
          },
          {
            to: 'style-guide',
            show: canTeamAdmin,
            title: 'Style Guide',
          },
          {
            to: 'policy-guide',
            show: canTeamAdmin,
            title: 'Policy Guide',
          },
          {
            to: 'templates',
            show: canSeeTemplates,
            title: 'Templates',
          },
          {
            to: 'organization-settings',
            show: canTeamAdmin && !noEditorEnabled,
            title: 'Settings',
          },
        ],
      },
    ];
  });

  const settingsNavLinks = computed(() => {
    const {
      canSuperuser,
      userId,
      projectId,
      hasProject,
      inviteKey,
      accessLevel,
    } = platform.value;

    const isProductionEnv = ['production', 'test-app'].includes(getEnvironment());

    return [
      {
        name: 'Datapeople',
        showSection: true,
        links: [
          {
            show: canSuperuser,
            href: '/admin/project',
            title: 'Admin Console',
          },
          {
            show: Boolean(canSuperuser && userId),
            href: `/admin/user/update/${userId}`,
            title: 'User Settings',
          },
          {
            show: Boolean(canSuperuser && projectId),
            href: `/admin/project/${accessLevel > 1 ? 'update' : 'view'}/${projectId}`,
            title: 'Project Settings',
          },
          {
            to: 'content-snapshot',
            show: canSuperuser && hasProject,
            title: 'Public Content Report',
            inviteKey,
          },
          {
            to: 'language-snapshot',
            show: canSuperuser && hasProject,
            title: 'Public Language Report',
            inviteKey,
          },
          {
            show: Boolean(currentJobId.value && canSuperuser),
            href: `/admin/job?JobSearch[id]=${currentJobId.value}`,
            title: 'Job Admin',
          },
        ],
      },

      settingsBreadCrumbsLinks.value[0],

      {
        name: 'Switch me to...',
        showSection: true,
        links: [
          {
            show: canSuperuser,
            href: '/admin/project/switch?project_name=Sales Demo',
            title: 'Sales Demo',
          },
          {
            show: canSuperuser,
            href: '/admin/project/switch?project_name=Job Architecture Demo',
            title: 'Job Architecture Demo',
          },
          {
            show: canSuperuser,
            href: '/admin/project/switch?project_name=Greenhouse Demo',
            title: 'Greenhouse Demo',
          },
          {
            show: canSuperuser,
            href: '/admin/project/switch?project_name=Workday Demo',
            title: 'Workday Demo',
          },
          {
            show: canSuperuser,
            href: '/admin/project/switch?project_name=ADP Demo',
            title: 'ADP Demo',
          },
          {
            show: canSuperuser,
            href: '/admin/project/switch?project_name=SmartRecruiters Demo',
            title: 'SmartRecruiters Demo',
          },
          {
            show: canSuperuser,
            href: '/admin/project/switch?project_name=Lever Demo',
            title: 'Lever Demo',
          },
          {
            show: canSuperuser,
            href: '/admin/project/switch?project_name=Workable Demo',
            title: 'Workable Demo',
          },
          {
            show: canSuperuser && !isProductionEnv,
            href: '/admin/project/switch?project_name=Insights Demo',
            title: 'Insights Demo',
          },
          {
            show: canSuperuser && !isProductionEnv,
            href: '/admin/project/switch?project_name=Greenhouse Non-Integrated',
            title: 'Greenhouse Non-Integrated',
          },
          {
            show: canSuperuser && !isProductionEnv,
            href: '/admin/project/switch?project_name=Workday Non-Integrated',
            title: 'Workday Non-Integrated',
          },
          {
            show: canSuperuser && !isProductionEnv,
            href: '/admin/project/switch?project_name=SmartRecruiters Non-Integrated',
            title: 'SmartRecruiters Non-Integrated',
          },
          {
            show: canSuperuser && !isProductionEnv,
            href: '/admin/project/switch?project_name=Lever Non-Integrated',
            title: 'Lever Non-Integrated',
          },
          {
            show: canSuperuser && !isProductionEnv,
            href: '/admin/project/switch?project_name=iCIMS Non-Integrated',
            title: 'iCIMS Non-Integrated',
          },
        ],
      },

      settingsBreadCrumbsLinks.value[1] || { showSection: false },
    ];
  });

  const reportingNavLinks = computed(() => {
    const {
      isApplicationReportingEnabled,
      showWeeklyReports,
      noEditorEnabled,
      isSuperuser,
    } = useAppAuthData().authData;

    const sections = [];

    if (isApplicationReportingEnabled) {
      sections.push(
        {
          showSection: isApplicationReportingEnabled,
          name: 'Process Insights',
          links: [
            {
              show: isApplicationReportingEnabled,
              to: 'executive-insights',
              title: 'Executive Insights',
            },
            {
              show: isApplicationReportingEnabled,
              to: 'pass-through',
              title: 'Pass-through Report',
            },
            {
              show: isApplicationReportingEnabled,
              to: 'drop-off',
              title: 'Drop-off Report',
            },
            {
              show: isApplicationReportingEnabled,
              to: 'gender-report',
              title: 'Gender Report',
            },
            {
              show: useAppAuthData().integrated && (isSuperuser || (isApplicationReportingEnabled
                && useAppAuthData().isDemographicSurveyReportVisible)),
              to: 'demographic-survey-report',
              title: 'Demographic Survey Report',
            },
            {
              show: isApplicationReportingEnabled,
              to: 'hiring-report',
              title: 'Hiring Report',
            },
          ],
        },
        {
          showSection: isApplicationReportingEnabled,
          name: 'Source Insights',
          links: [
            {
              show: isApplicationReportingEnabled,
              to: 'overall-report',
              title: 'Overall Report',
            },
            {
              show: isApplicationReportingEnabled,
              to: 'inbound-report',
              title: 'Inbound Report',
            },
            {
              show: isApplicationReportingEnabled,
              to: 'outbound-report',
              title: 'Outbound Report',
            },
          ],
        },
      );
    }

    sections.push({
      showSection: true,
      name: 'Actionable Insights',
      links: [
        {
          show: true,
          to: 'compliance-report',
          title: 'Compliance Report',
        },
        {
          show: true,
          to: 'employer-brand-report',
          title: 'Employer Brand Report',
        },
        {
          show: true,
          to: 'job-performance-report',
          title: 'Job Performance Report',
        }],
    });

    sections.push({
      showSection: true,
      name: 'Operational Insights',
      links: [{
        show: showWeeklyReports,
        to: 'content-snapshot',
        title: 'Content Snapshot',
      },
      {
        show: showWeeklyReports,
        to: 'language-snapshot',
        title: 'Language Snapshot',
      },
      {
        show: showWeeklyReports,
        to: 'weekly-snapshot',
        title: 'Weekly Snapshot',
      },
      {
        show: true,
        to: 'recruiting-performance',
        title: 'Recruiting Performance Report',
      },
      {
        show: true,
        to: 'usage',
        title: 'Usage Report',
        isUpgrade: noEditorEnabled,
      }],
    });

    return sections;
  });

  /* ==============
    actions
  ============== */

  const setPlatformCurrentJobId = (id) => {
    currentJobId.value = id;
  };

  const initPlatform = async (jobId, { setInitFieldsAction }) => {
    try {
      const responseJSON = await recruitInitNavbar({ jobId });

      if (responseJSON.success) {
        const {
          isApplicationReportingEnabled,
          templateList,
          showWeeklyReports,
        } = responseJSON.data || {};

        setInitFieldsAction({
          isApplicationReportingEnabled, templateList, showWeeklyReports,
        });

        // these fields have been moved to editor vuex module
        ['isApplicationReportingEnabled', 'templateList', 'showWeeklyReports'].forEach((field) => {
          delete responseJSON.data[field];
        });

        platform.value = { ...responseJSON.data };
        platformInitialized.value = true;
        setPlatformCurrentJobId(jobId);
      }
    } catch (error) {
      if (error.response?.code === 'project_not_found') {
        addErrorSnackbar({ text: 'Project not found' });
      } else {
        throw error;
      }
    }
  };

  return {
    /* state */
    platform,
    getHelpLinks,
    platformInitialized,
    currentJobId,

    /* computed */
    dashboardNavLinks,
    reportingNavLinks,
    settingsNavLinks,
    settingsBreadCrumbsLinks,

    /* actions */
    initPlatform,
    setPlatformCurrentJobId,
  };
});
